import React, { useCallback, useEffect, useState } from 'react'

import { Box, useScreenClass } from '@vp/swan'

import { QuantitySelectorLoader } from './QuantitySelectorLoader'
import QuantityPrice from './QuantityPrice'
import { useQuantity } from '../../contexts/QuantityContext'
import { useInventory } from '../../../root/contexts/InventoryContext'
import { usePricingContext } from '../../../root/contexts/PricingContext'
import { OutOfStockToast } from '../OutOfStockToast'
import type { HardGoodAvailability } from '../../../../types/Inventory'
import { PricingSliderLoader } from './PricingSliderLoader'
import { useProject } from '../../../root/contexts/ProjectContext'
import { scrollToTopToast } from '../../../../utils/ScrollHelpers'
import PricingSliderWrapper from './PricingSliderWrapper'
import { useAlertState } from '../../../root/contexts/AlertContext'
import { AddToCartValidationToast } from '../AddToCartValidationToast'
import { useTracking } from '../../../root/contexts/TrackingContext'
import { TrackingLabel } from '../../../root/contexts/TrackingContext/TrackingConstants'

export const ConnectedHardGoodQuantitySelector: React.FC = () => {
  const inventoryState = useInventory()
  const { isInitialized: isPricingContextInitialized } = usePricingContext()
  const {
    minimumOrderQuantity,
    maximumOrderQuantity,
    quantityIncrement,
    singleValueSize,
    productKey,
    productVersion,
    lowestPriceAttributes,
    projectCount,
    projectNumber,
    isPrimaryProject,
    workId,
    mpvId,
    productName,
    productViewTracked,
    setProductViewTracked,
  } = useProject()
  const { initialInputQuantity, inputQuantity, updateQuantity } = useQuantity()
  const { trackProductViewed } = useTracking()
  const screenClass = useScreenClass()
  const { projectQtyErrors } = useAlertState()
  const [showAddToCartValidationToast, setShowAddToCartValidationToast] = useState(false)

  const isLoading = !inventoryState.isInventoryStateInitialized || !isPricingContextInitialized

  useEffect(() => {
    if (productViewTracked) return

    if (!isLoading) {
      trackProductViewed(TrackingLabel.QSP_PRODUCT_VIEWED, mpvId, productName, productKey, productVersion)
      setProductViewTracked(true)
    }
  }, [isLoading])

  useEffect(() => {
    const projectHasInvalidQty = projectQtyErrors.includes(workId)
    setShowAddToCartValidationToast(projectHasInvalidQty)
  }, [projectQtyErrors, projectQtyErrors.length, workId])

  const onChange = useCallback(
    (quantity: number, isValid: boolean): void => {
      updateQuantity({ quantities: quantity, total: quantity }, isValid)
      if (showAddToCartValidationToast && isValid) {
        setShowAddToCartValidationToast(false)
      }
    },
    [showAddToCartValidationToast, updateQuantity],
  )

  const onHardgoodQuantityChange = useCallback(
    (newQuantity: number | undefined) => {
      const salableHardgoodQuantity = inventoryState.inventory as HardGoodAvailability

      if (newQuantity) {
        onChange(newQuantity, newQuantity >= minimumOrderQuantity && newQuantity <= (salableHardgoodQuantity?.numAvailable ?? 0))
      }
    },
    [inventoryState.inventory, minimumOrderQuantity, onChange],
  )

  const isOutOfStock = (availability: HardGoodAvailability): boolean => {
    return availability.numAvailable < minimumOrderQuantity
  }

  const getExpectedDate = (availability: HardGoodAvailability): Date | undefined => {
    return availability.expectedDate
  }

  useEffect(() => {
    // For hard goods we need to trigger a change event since the slider
    // doesn't do that for us on the initial render
    if (inventoryState.isInventoryStateInitialized && (inputQuantity as number) > 0) {
      onHardgoodQuantityChange(inputQuantity as number)
    }
  }, [inventoryState.isInventoryStateInitialized])

  useEffect(() => {
    // Primary project is OOS, scroll "nearest"
    if (!isLoading && screenClass === 'xs' && projectCount === 1) {
      scrollToTopToast('qsp-project-quantity-section-alert', 1000, 'nearest')
    }

    // OOS projects will have an invalid quantity/add to cart validation toast,
    // scroll "center" to make sure the related products title doesn't block it.
    else if (showAddToCartValidationToast) {
      scrollToTopToast('qsp-project-quantity-section-alert', 0, 'center')
    }
  }, [isLoading, screenClass, showAddToCartValidationToast, projectQtyErrors])

  return (
    <>
      {isLoading && (
        <Box className="qsp-project-quantity-section qsp-project-quantity-section-grey" backgroundColor="strong">
          <QuantitySelectorLoader />
        </Box>
      )}
      {!isLoading && (
        <>
          {!isPrimaryProject && showAddToCartValidationToast && (
            <Box className="qsp-project-quantity-section qsp-project-quantity-section-alert" my={3}>
              <AddToCartValidationToast key={projectNumber} projectNumber={projectNumber} />
            </Box>
          )}
          {isOutOfStock(inventoryState.inventory as HardGoodAvailability) && (
            <Box className="qsp-project-quantity-section qsp-project-quantity-section-alert" my={3}>
              <OutOfStockToast expectedDate={getExpectedDate(inventoryState.inventory as HardGoodAvailability)} />
            </Box>
          )}
          <QuantityPrice />
          {/** this wrapper Card is to support extend the background to the edges */}
          <Box className="qsp-project-quantity-section qsp-project-quantity-section-grey" data-testid="hardgood-quantity-selector" backgroundColor="strong">
            {isLoading && <PricingSliderLoader />}
            {!isLoading && (
              <PricingSliderWrapper
                minimumOrderQuantity={minimumOrderQuantity}
                maximumOrderQuantity={maximumOrderQuantity}
                quantityIncrement={quantityIncrement}
                productKey={productKey}
                productVersion={productVersion}
                lowestPriceAttributes={lowestPriceAttributes}
                initialInputQuantity={initialInputQuantity as number}
                stock={(inventoryState.inventory as HardGoodAvailability).numAvailable}
                onHardgoodQuantityChange={onHardgoodQuantityChange}
                singleValueSize={singleValueSize}
              />
            )}
          </Box>
        </>
      )}
    </>
  )
}
